import React, { useEffect, useState } from 'react';
import { Button, DatePicker, Form, Input, InputNumber, Modal, Popconfirm, Select, message } from 'antd';
import dayjs from 'dayjs';
import api from '../common/api';
import { useCashFlow } from '../common/CashFlowContext';

export default function ExpenseFormModal({ open, expense, onClose, onSaved }) {
  const { currentUserId } = useCashFlow();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [methods, setMethods] = useState([]);
  const isEdit = !!expense;

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [catRes, methodRes] = await Promise.all([
        api.get('/categories'),
        api.get('/methods'),
      ]);
      setCategories(catRes.data);
      setMethods(methodRes.data);
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (expense) {
      form.setFieldsValue({
        used_at: dayjs(expense.used_at),
        amount: expense.amount,
        title: expense.title,
        category_id: expense.category_id,
        method_id: expense.method_id,
        memo: expense.memo || '',
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ used_at: dayjs() });
    }
  }, [open, expense, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        user_id: currentUserId,
        amount: values.amount,
        category_id: values.category_id,
        method_id: values.method_id,
        title: values.title,
        memo: values.memo || null,
        used_at: values.used_at.format('YYYY-MM-DD'),
      };
      if (isEdit) {
        await api.put(`/expenses/${expense.id}`, payload);
      } else {
        await api.post('/expenses', payload);
      }
      onSaved?.();
      onClose();
    } catch (err) {
      if (err?.errorFields) return;
      message.error('저장 실패: ' + (err.message || '알 수 없는 오류'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/expenses/${expense.id}`);
      onSaved?.();
      onClose();
    } catch (err) {
      message.error('삭제 실패: ' + (err.message || '알 수 없는 오류'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? '지출 수정' : '지출 추가'}
      onCancel={onClose}
      footer={[
        isEdit && (
          <Popconfirm key="del" title="이 지출을 삭제할까요?" onConfirm={handleDelete} okText="삭제" cancelText="취소">
            <Button danger loading={saving}>삭제</Button>
          </Popconfirm>
        ),
        <Button key="cancel" onClick={onClose}>취소</Button>,
        <Button key="ok" type="primary" loading={saving} onClick={handleSubmit}>
          {isEdit ? '수정' : '저장'}
        </Button>,
      ].filter(Boolean)}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item name="used_at" label="날짜" rules={[{ required: true, message: '날짜를 선택해 주세요' }]}>
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
        </Form.Item>
        <Form.Item name="amount" label="금액 (원)" rules={[{ required: true, message: '금액을 입력해 주세요' }]}>
          <InputNumber
            style={{ width: '100%' }}
            min={0}
            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(v) => v.replace(/,/g, '')}
          />
        </Form.Item>
        <Form.Item name="title" label="제목" rules={[{ required: true, message: '제목을 입력해 주세요' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="category_id" label="카테고리" rules={[{ required: true, message: '카테고리를 선택해 주세요' }]}>
          <Select
            options={categories.map((c) => ({ label: c.name, value: c.id }))}
            placeholder="카테고리 선택"
          />
        </Form.Item>
        <Form.Item name="method_id" label="결제수단" rules={[{ required: true, message: '결제수단을 선택해 주세요' }]}>
          <Select
            options={methods.map((m) => ({ label: m.name, value: m.id }))}
            placeholder="결제수단 선택"
          />
        </Form.Item>
        <Form.Item name="memo" label="메모 (선택)">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
